import {push} from "connected-react-router";
import {setSubmitSucceeded, startSubmit, stopSubmit} from "redux-form";
import {all, call, fork, put, select, take} from "redux-saga/effects";
import {IPastCaseProjectRepository} from "src/domain/repository/past_case_project_repository";
import {createPastCaseProjectAPI} from "src/infra/api/service/past_case_project_api";
import {createPublicAPI} from "src/infra/api/service/public_api";
import {NotificationClient} from "../../config/view_const/notification";
import {AppNotificationType} from "../../domain/model/app_notification_message";
import {ClientBookmark} from "../../domain/model/client_bookmark";
import {Company} from "../../domain/model/company";
import {Customer} from "../../domain/model/customer";
import {MessageRoom} from "../../domain/model/message_room";
import {PastCaseProject} from "../../domain/model/past_case_project";
import {Occupation, Project} from "../../domain/model/project";
import {SearchHighlight} from "../../domain/model/search_highlight";
import {User} from "../../domain/model/user";
import {IClientBookmarkRepository} from "../../domain/repository/client_bookmark_repository";
import {IClientBrowsingHistoryRepository} from "../../domain/repository/client_browsing_history_repository";
import {ICompanyRepository} from "../../domain/repository/company_repository";
import {ICustomerRepository, TSearchCustomerRequest,} from "../../domain/repository/customer_repository";
import {IJobCareerRepository, TJobCareerWithCustomerID,} from "../../domain/repository/job_career_repository";
import {IProjectRepository} from "../../domain/repository/project_repository";
import {IScoutRepository} from "../../domain/repository/scout_repository";
import {createApiClient, IApiClient} from "../../infra/api/client";
import {api, entity} from "../../infra/api/rpc/api";
import {createClientBookmarkAPI} from "../../infra/api/service/client_bookmark_api";
import {createClientBrowsingHistoryAPI} from "../../infra/api/service/client_browsing_history_api";
import {createCompanyAPI} from "../../infra/api/service/company_api";
import {createCustomerAPI} from "../../infra/api/service/customer_api";
import {createJobCareerAPI} from "../../infra/api/service/job_career_api";
import {createProjectAPI} from "../../infra/api/service/project_api";
import {createScoutAPI} from "../../infra/api/service/scout_api";
import {extractOccupationsFromJobCareers} from "../../util/helper_job_career";
import {
    createAppNotificationMessageActionCreator,
    IAppNotificationMessageActionCreator,
} from "../action/app_notification_message_action";
import {
    ClientScoutCustomerActionType,
    createClientScoutCustomerActionCreator,
    IClientScoutCustomerActionCreator,
    IRequestCopyHighlightsAction,
    IRequestCreateClientBookmarkAction,
    IRequestCreateScoutCustomerAction,
    IRequestDeleteClientBookmarkAction,
    IRequestGetCustomerAction,
} from "../action/client_scout_customer_action";
import {AddAppNotificationRequest} from "../request/app_notification_message_request";
import {
    CopyHighlightsByClientResponse,
    CreateClientBookmarkByClientResponse,
    CreateScoutCustomerByClientResponse,
    DeleteClientBookmarkByClientResponse,
    DeleteHighlightsByClientResponse,
    GetCustomerByClientResponse,
} from "../response/scout_response";
import {AppState} from "../store/app_state";
import {AuthState} from "../store/auth_state";
import {handleErrorForHandler} from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const companyRepository: ICompanyRepository = createCompanyAPI(apiClient);
const jobcareerRepository: IJobCareerRepository = createJobCareerAPI(apiClient);
const pastCaseProjectRepository: IPastCaseProjectRepository =
    createPastCaseProjectAPI(apiClient);
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const scoutRepository: IScoutRepository = createScoutAPI(apiClient);
const clientBookmarkRepository: IClientBookmarkRepository =
    createClientBookmarkAPI(apiClient);
const clientBrowsingHistoryRepository: IClientBrowsingHistoryRepository =
    createClientBrowsingHistoryAPI(apiClient);

const actionCreator: IClientScoutCustomerActionCreator =
    createClientScoutCustomerActionCreator();
const actionCreatorForNotification: IAppNotificationMessageActionCreator =
    createAppNotificationMessageActionCreator();
const publicAPI = createPublicAPI(apiClient);

function* handleGetCustomerInClientScoutCustomer() {
    while (true) {
        try {
            const action: IRequestGetCustomerAction = yield take(
                ClientScoutCustomerActionType.REQUEST_GET_CUSTOMER
            );

            const authSelector = (state: AppState) => state.authReducer;
            const _authState: AuthState = yield select(authSelector);
            const user: User | null = _authState.user;
            const companyID = user ? (user.client ? user.client.companyID : "") : "";

            let customer: Customer;
            let company: Company;
            let jobCareersWithCustomerID: TJobCareerWithCustomerID[];
            let clientBookmarks: ClientBookmark[];
            let pastCaseProjects: PastCaseProject[];

            if (user && user.role !== 0) {
                [
                    customer,
                    company,
                    jobCareersWithCustomerID,
                    clientBookmarks,
                    pastCaseProjects,
                ] = yield all([
                    call(getCustomer, action.item.customerId),
                    call(getCompany, companyID),
                    call(getJobCareerAllByCustomerIDs, [action.item.customerId]),
                    call(getClientBookmarks),
                    call(getAllByClientPastCaseProject, action.item.customerId),
                ]);
                const jobCareers = jobCareersWithCustomerID[0].jobCareers;

                const res: GetCustomerByClientResponse =
                    new GetCustomerByClientResponse();
                res.customer = customer;
                res.company = company;
                res.jobCareers = jobCareers;
                res.projects = [];
                res.clientBookmarks = clientBookmarks;
                res.pastCaseProjects = pastCaseProjects;
                res.sameOccupationCustomers = [];
                res.jobCareersWithSameOccupationCustomerID = [];
                yield put(actionCreator.callbackGetCustomerAction(true, res));

                let projectList: Project[] = [];
                projectList = yield call(
                    getProjectAllByClientExcludeAlreadyEntry,
                    action.item.customerId
                );
                res.projects = projectList;
                yield put(actionCreator.callbackGetCustomerAction(true, res));

                yield call(createClientBrowsingHistory, action.item.customerId)


                let sameOccupationCustomers: Customer[] = [];
                const occupations: Occupation[] =
                    extractOccupationsFromJobCareers(jobCareers);
                // 動的に並列処理の書き方がわからなかったため場合分けで書いている。
                if (occupations.length === 0) {
                    const searchCustomerRequest: TSearchCustomerRequest = {
                        page: 1,
                        offset: 6,
                        query: "",
                        singleSkillTagIDs: [],
                        multiSkillTagIDs: [],
                        occupationSkillTagCategory:
                        entity.OccupationSkillTag.Category.Category_Unknown,
                        occupationSkillTagId: "",
                        gender: entity.User.Gender.Gender_Unknown,
                        age: api.CustomerAgeSearchType.Unknown,
                        workIntention:
                        entity.Customer.WorkIntention.WorkIntention_Unknown,
                        fullTimePossibility:
                        entity.Customer.FullTimePossibility.FullTimePossibility_Unknown,
                    };
                    const getCustomers = yield call(
                        GetAllBySearchCustomer,
                        searchCustomerRequest
                    );
                    sameOccupationCustomers = [
                        ...sameOccupationCustomers,
                        ...getCustomers,
                    ];
                }

                if (occupations.length === 1) {
                    const searchCustomerRequest: TSearchCustomerRequest = {
                        page: 1,
                        offset: 6,
                        query: "",
                        singleSkillTagIDs: [],
                        multiSkillTagIDs: [],
                        occupationSkillTagCategory:
                        entity.OccupationSkillTag.Category.Category_Unknown,
                        occupationSkillTagId: "",
                        gender: entity.User.Gender.Gender_Unknown,
                        age: api.CustomerAgeSearchType.Unknown,
                        workIntention:
                        entity.Customer.WorkIntention.WorkIntention_Unknown,
                        fullTimePossibility:
                        entity.Customer.FullTimePossibility.FullTimePossibility_Unknown,
                    };
                    const getCustomers = yield call(
                        GetAllBySearchCustomer,
                        searchCustomerRequest
                    );
                    sameOccupationCustomers = [
                        ...sameOccupationCustomers,
                        ...getCustomers,
                    ];
                }

                if (occupations.length >= 2) {
                    const searchCustomerRequest1: TSearchCustomerRequest = {
                        page: 1,
                        offset: 6,
                        query: "",
                        singleSkillTagIDs: [],
                        multiSkillTagIDs: [],
                        occupationSkillTagCategory:
                        entity.OccupationSkillTag.Category.Category_Unknown,
                        occupationSkillTagId: "",
                        gender: entity.User.Gender.Gender_Unknown,
                        age: api.CustomerAgeSearchType.Unknown,
                        workIntention:
                        entity.Customer.WorkIntention.WorkIntention_Unknown,
                        fullTimePossibility:
                        entity.Customer.FullTimePossibility.FullTimePossibility_Unknown,
                    };
                    const searchCustomerRequest2: TSearchCustomerRequest = {
                        page: 1,
                        offset: 6,
                        query: "",
                        singleSkillTagIDs: [],
                        multiSkillTagIDs: [],
                        occupationSkillTagCategory:
                        entity.OccupationSkillTag.Category.Category_Unknown,
                        occupationSkillTagId: "",
                        gender: entity.User.Gender.Gender_Unknown,
                        age: api.CustomerAgeSearchType.Unknown,
                        workIntention:
                        entity.Customer.WorkIntention.WorkIntention_Unknown,
                        fullTimePossibility:
                        entity.Customer.FullTimePossibility.FullTimePossibility_Unknown,
                    };
                    const [occupationCustomers1, occupationCustomers2] = yield all([
                        call(GetAllBySearchCustomer, searchCustomerRequest1),
                        call(GetAllBySearchCustomer, searchCustomerRequest2),
                    ]);
                    const occupationCustomers = [
                        ...occupationCustomers1,
                        ...occupationCustomers2,
                    ];
                    // 重複排除のためのIDをkey情報の連想配列化
                    const hashCustomer: { [id: string]: Customer } = {};
                    occupationCustomers.forEach((c) => {
                        hashCustomer[c.id] = c;
                    });

                    sameOccupationCustomers = Object.values(hashCustomer);
                }

                // 表示されているcustomerと同じID情報のcustomerの削除
                // customer の数を上限５へ制限
                const sameOccupationCustomersExclusionOrigin = sameOccupationCustomers
                    .filter(
                        (sameOccupationCustomer) =>
                            sameOccupationCustomer.id !== customer.id
                    )
                    .splice(0, 5);

                const sameOccupationCustomerIDs =
                    sameOccupationCustomersExclusionOrigin.map(
                        (sameOccupationCustomerExclusionOrigin) =>
                            sameOccupationCustomerExclusionOrigin.id
                    );

                const jobCareersWithSameOccupationCustomerID = yield call(
                    getJobCareerAllByCustomerIDs,
                    sameOccupationCustomerIDs
                );

                res.sameOccupationCustomers = sameOccupationCustomersExclusionOrigin;
                res.jobCareersWithSameOccupationCustomerID =
                    jobCareersWithSameOccupationCustomerID;

                yield put(actionCreator.callbackGetCustomerAction(true, res));
            } else {
                [customer, jobCareersWithCustomerID, pastCaseProjects] = yield all([
                    call(getCustomer, action.item.customerId),
                    call(getJobCareerAllByCustomerIDs, [action.item.customerId]),
                    call(getAllByClientPastCaseProject, action.item.customerId),
                ]);
                const jobCareers = jobCareersWithCustomerID[0].jobCareers;

                const res: GetCustomerByClientResponse =
                    new GetCustomerByClientResponse();
                res.customer = customer;
                res.jobCareers = jobCareers;
                res.pastCaseProjects = pastCaseProjects;
                yield put(actionCreator.callbackGetCustomerAction(true, res));
            }
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                actionCreator.callbackGetCustomerAction(
                    false,
                    undefined,
                    error.toString()
                )
            );
        }
    }
}

function* handleCreateScoutInClientScoutCustomer() {
    while (true) {
        try {
            const action: IRequestCreateScoutCustomerAction = yield take(
                ClientScoutCustomerActionType.REQUEST_CREATE_SCOUT_CUSTOMER
            );
            yield put(startSubmit("ClientScoutCustomerMainScoutForm"));

            const messageRoom: MessageRoom = yield call(
                createScout,
                action.item.req.customerID,
                action.item.req.projectID,
                action.item.req.message
            );

            const res: CreateScoutCustomerByClientResponse =
                new CreateScoutCustomerByClientResponse();
            res.messageRoom = messageRoom;

            yield put(actionCreator.callbackCreateScoutCustomerAction(true, res));
            yield put(stopSubmit("ClientScoutCustomerMainScoutForm"));
            yield put(setSubmitSucceeded("ClientScoutCustomerMainScoutForm"));
            yield put(push(`/client/messages`));

            const notificationReq: AddAppNotificationRequest =
                new AddAppNotificationRequest();
            notificationReq.type = AppNotificationType.INFO;
            notificationReq.message = NotificationClient.successCreateScout;
            yield put(actionCreatorForNotification.requestAddAction(notificationReq));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                actionCreator.callbackCreateScoutCustomerAction(
                    false,
                    undefined,
                    error.toString()
                )
            );
            yield put(
                stopSubmit("ClientScoutCustomerMainScoutForm", {
                    _error: error.toString(),
                })
            );
        }
    }
}

function* handleCreateClientBookmarkInClientScoutCustomer() {
    while (true) {
        try {
            const action: IRequestCreateClientBookmarkAction = yield take(
                ClientScoutCustomerActionType.REQUEST_CREATE_CLIENT_BOOKMARK
            );
            yield call(createClientBookmark, action.item.customerId);
            const clientBookmarks: ClientBookmark[] = yield call(getClientBookmarks);
            const res: CreateClientBookmarkByClientResponse =
                new CreateClientBookmarkByClientResponse();
            res.clientBookmarks = clientBookmarks;
            yield put(actionCreator.callbackCreateClientBookmarkAction(true, res));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                actionCreator.callbackCreateClientBookmarkAction(
                    false,
                    undefined,
                    error.toString()
                )
            );
        }
    }
}

function* handleDeleteClientBookmarkInClientScoutCustomer() {
    while (true) {
        try {
            const action: IRequestDeleteClientBookmarkAction = yield take(
                ClientScoutCustomerActionType.REQUEST_DELETE_CLIENT_BOOKMARK
            );
            yield call(deleteClientBookmark, action.item.customerId);
            const clientBookmarks: ClientBookmark[] = yield call(getClientBookmarks);
            const res: DeleteClientBookmarkByClientResponse =
                new DeleteClientBookmarkByClientResponse();
            res.clientBookmarks = clientBookmarks;
            yield put(actionCreator.callbackDeleteClientBookmarkAction(true, res));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                actionCreator.callbackDeleteClientBookmarkAction(
                    false,
                    undefined,
                    error.toString()
                )
            );
        }
    }
}

function* handleCopyHighlightsInClientScoutCustomer() {
    while (true) {
        try {
            const action: IRequestCopyHighlightsAction = yield take(
                ClientScoutCustomerActionType.REQUEST_COPY_HIGHLIGHTS
            );
            const highlights: SearchHighlight[] = action.item.highlights;
            const res: CopyHighlightsByClientResponse =
                new CopyHighlightsByClientResponse();
            res.highlights = highlights;
            yield put(actionCreator.callbackCopyHighlightsAction(true, res));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                actionCreator.callbackCopyHighlightsAction(
                    false,
                    undefined,
                    error.toString()
                )
            );
        }
    }
}

function* handleDeleteHighlightsInClientScoutCustomer() {
    while (true) {
        try {
            yield take(ClientScoutCustomerActionType.REQUEST_DELETE_HIGHLIGHTS);
            const res: DeleteHighlightsByClientResponse =
                new DeleteHighlightsByClientResponse();
            yield put(actionCreator.callbackDeleteHighlightsAction(true, res));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                actionCreator.callbackDeleteHighlightsAction(
                    false,
                    undefined,
                    error.toString()
                )
            );
        }
    }
}

const getCustomer = (customerID: string): Promise<Customer> => {
    return publicAPI.getCustomer(customerID);
};

const getCompany = (companyId: string): Promise<Company> => {
    return companyRepository.get(companyId);
};

const getJobCareerAllByCustomerIDs = (
    ids: string[]
): Promise<TJobCareerWithCustomerID[]> => {
    return publicAPI.getAllJobCareerByCustomerIDs(ids);
};

const getAllByClientPastCaseProject = (
    customerId: string
): Promise<PastCaseProject[]> => {
    return publicAPI.getAllPastCaseProject(customerId);
};

const getProjectAllByClientExcludeAlreadyEntry = (
    customerId: string
): Promise<Project[]> => {
    return projectRepository.getAllByClientExcludeAlreadyEntry(customerId);
};

const createScout = (
    customerID: string,
    projectID: string,
    message: string
): Promise<MessageRoom> => {
    return scoutRepository.create(customerID, projectID, message);
};

const GetAllBySearchCustomer = (r: TSearchCustomerRequest) => {
    return customerRepository.getAllBySearch(
        r.query,
        r.singleSkillTagIDs,
        r.multiSkillTagIDs,
        r.occupationSkillTagCategory,
        r.occupationSkillTagId,
        r.gender,
        r.age,
        r.workIntention,
        r.fullTimePossibility,
        r.page,
        r.offset
    );
};

const getClientBookmarks = (): Promise<ClientBookmark[]> => {
    return clientBookmarkRepository.getAllByClient();
};

const createClientBookmark = (customerID: string): Promise<void> => {
    return clientBookmarkRepository.create(customerID);
};

const deleteClientBookmark = (customerID: string): Promise<void> => {
    return clientBookmarkRepository.delete(customerID);
};

const createClientBrowsingHistory = (customerID: string): Promise<void> => {
    return clientBrowsingHistoryRepository.create(customerID);
};

export {
    handleCopyHighlightsInClientScoutCustomer,
    handleCreateClientBookmarkInClientScoutCustomer,
    handleCreateScoutInClientScoutCustomer,
    handleDeleteClientBookmarkInClientScoutCustomer,
    handleDeleteHighlightsInClientScoutCustomer,
    handleGetCustomerInClientScoutCustomer,
};



// WEBPACK FOOTER //
// ./src/presentation/handler/client_scout_customer_handler.ts